import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

// Status badge component
function StatusBadge({ status }) {
  const statusConfig = {
    draft: { color: 'bg-gray-500/20 text-gray-300', icon: DocumentTextIcon, label: 'Draft' },
    sent: { color: 'bg-blue-500/20 text-blue-300', icon: PaperAirplaneIcon, label: 'Sent' },
    paid: { color: 'bg-emerald-500/20 text-emerald-300', icon: CheckCircleIcon, label: 'Paid' },
    overdue: { color: 'bg-red-500/20 text-red-300', icon: ExclamationTriangleIcon, label: 'Overdue' },
    cancelled: { color: 'bg-surface-500/20 text-surface-400', icon: XCircleIcon, label: 'Cancelled' }
  };

  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
}

// Format currency
function formatCurrency(amount, symbol = 'Rp', code = 'IDR') {
  if (code === 'IDR') {
    return `${symbol} ${amount?.toLocaleString('id-ID') || 0}`;
  }
  return `${symbol}${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
}

// Format date
function formatDate(date) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Invoice Details Modal
function InvoiceDetailsModal({ isOpen, onClose, invoice, onAction }) {
  if (!invoice) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <Dialog.Title className="text-xl font-semibold text-surface-100">
                Invoice {invoice.invoiceNumber}
              </Dialog.Title>
              <p className="text-sm text-surface-400 mt-1">
                {invoice.billingPeriodDisplay}
              </p>
            </div>
            <StatusBadge status={invoice.status} />
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="bg-surface-700/30 rounded-lg p-4">
              <h4 className="text-sm font-medium text-surface-300 mb-3">Bill To</h4>
              <p className="font-semibold text-surface-100">{invoice.customerSnapshot?.companyName}</p>
              {invoice.customerSnapshot?.billingContact && (
                <p className="text-sm text-surface-400">Attn: {invoice.customerSnapshot.billingContact}</p>
              )}
              {invoice.customerSnapshot?.invoiceEmail && (
                <p className="text-sm text-surface-400">{invoice.customerSnapshot.invoiceEmail}</p>
              )}
            </div>
            <div className="bg-surface-700/30 rounded-lg p-4">
              <h4 className="text-sm font-medium text-surface-300 mb-3">Invoice Details</h4>
              <div className="space-y-1 text-sm">
                <p><span className="text-surface-400">Issue Date:</span> <span className="text-surface-200">{formatDate(invoice.issueDate)}</span></p>
                <p><span className="text-surface-400">Due Date:</span> <span className="text-surface-200">{formatDate(invoice.dueDate)}</span></p>
                {invoice.sentAt && (
                  <p><span className="text-surface-400">Sent:</span> <span className="text-surface-200">{formatDate(invoice.sentAt)} to {invoice.sentTo}</span></p>
                )}
                {invoice.paidAt && (
                  <p><span className="text-surface-400">Paid:</span> <span className="text-emerald-400">{formatDate(invoice.paidAt)}</span></p>
                )}
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-surface-300 mb-3">Line Items</h4>
            <div className="bg-surface-700/30 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-surface-700/50">
                    <th className="text-left text-xs font-medium text-surface-400 p-3">Description</th>
                    <th className="text-center text-xs font-medium text-surface-400 p-3">Qty</th>
                    <th className="text-right text-xs font-medium text-surface-400 p-3">Unit Price</th>
                    <th className="text-right text-xs font-medium text-surface-400 p-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems?.map((item, idx) => (
                    <tr key={idx} className="border-t border-surface-600">
                      <td className="p-3 text-surface-200">{item.description}</td>
                      <td className="p-3 text-center text-surface-300">{item.quantity}</td>
                      <td className="p-3 text-right text-surface-300">
                        {formatCurrency(item.unitPrice, invoice.currency?.symbol, invoice.currency?.code)}
                      </td>
                      <td className="p-3 text-right text-surface-200">
                        {formatCurrency(item.amount, invoice.currency?.symbol, invoice.currency?.code)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-surface-600">
                    <td colSpan="3" className="p-3 text-right text-surface-400">Subtotal</td>
                    <td className="p-3 text-right text-surface-200">
                      {formatCurrency(invoice.subtotal, invoice.currency?.symbol, invoice.currency?.code)}
                    </td>
                  </tr>
                  {invoice.tax > 0 && (
                    <tr>
                      <td colSpan="3" className="p-3 text-right text-surface-400">Tax ({invoice.taxRate}%)</td>
                      <td className="p-3 text-right text-surface-200">
                        {formatCurrency(invoice.tax, invoice.currency?.symbol, invoice.currency?.code)}
                      </td>
                    </tr>
                  )}
                  <tr className="bg-surface-700/50">
                    <td colSpan="3" className="p-3 text-right font-semibold text-surface-200">Total</td>
                    <td className="p-3 text-right font-semibold text-primary-400 text-lg">
                      {formatCurrency(invoice.total, invoice.currency?.symbol, invoice.currency?.code)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Payment Info */}
          {invoice.status === 'paid' && invoice.paymentReference && (
            <div className="mb-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
              <h4 className="text-sm font-medium text-emerald-300 mb-2">Payment Information</h4>
              <p className="text-sm text-surface-300">
                <span className="text-surface-400">Reference:</span> {invoice.paymentReference}
              </p>
              {invoice.paymentNotes && (
                <p className="text-sm text-surface-300 mt-1">
                  <span className="text-surface-400">Notes:</span> {invoice.paymentNotes}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-surface-700">
            <button onClick={onClose} className="btn-secondary flex-1">
              Close
            </button>
            <button
              onClick={() => onAction('download', invoice)}
              className="btn-secondary"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Download PDF
            </button>
            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
              <>
                <button
                  onClick={() => onAction('resend', invoice)}
                  className="btn-secondary"
                >
                  <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                  Send
                </button>
                <button
                  onClick={() => onAction('markPaid', invoice)}
                  className="btn-primary"
                >
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  Mark Paid
                </button>
              </>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// Mark Paid Modal
function MarkPaidModal({ isOpen, onClose, invoice, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    reference: '',
    notes: '',
    paidAt: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (invoice) {
      setFormData(prev => ({
        ...prev,
        amount: invoice.total?.toString() || ''
      }));
    }
  }, [invoice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onConfirm(invoice._id, formData);
      onClose();
    } catch (error) {
      console.error('Error marking paid:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-md">
          <Dialog.Title className="text-lg font-semibold text-surface-100 mb-4">
            Mark Invoice as Paid
          </Dialog.Title>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Invoice
              </label>
              <p className="text-surface-200">{invoice.invoiceNumber}</p>
              <p className="text-sm text-surface-400">
                {formatCurrency(invoice.total, invoice.currency?.symbol, invoice.currency?.code)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Amount Paid
              </label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="input"
                step="any"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Payment Reference
              </label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className="input"
                placeholder="Bank transfer reference"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Payment Date
              </label>
              <input
                type="date"
                value={formData.paidAt}
                onChange={(e) => setFormData({ ...formData, paidAt: e.target.value })}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input"
                rows="2"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// Generate Invoice Modal
function GenerateInvoiceModal({ isOpen, onClose, companies, onGenerate }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyId: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() - 1 < 0 ? 11 : new Date().getMonth() - 1,
    sendEmail: true
  });

  // Adjust year if previous month was December
  useEffect(() => {
    if (new Date().getMonth() === 0) {
      setFormData(prev => ({
        ...prev,
        year: new Date().getFullYear() - 1,
        month: 11
      }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyId) {
      toast.error('Please select a company');
      return;
    }
    
    setLoading(true);
    try {
      await onGenerate(formData);
      onClose();
    } catch (error) {
      console.error('Error generating invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-md">
          <Dialog.Title className="text-lg font-semibold text-surface-100 mb-4">
            Generate Invoice
          </Dialog.Title>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Company
              </label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                className="input"
                required
              >
                <option value="">Select a company</option>
                {companies.map(company => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Month
                </label>
                <select
                  value={formData.month}
                  onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                  className="input"
                >
                  {months.map((month, idx) => (
                    <option key={idx} value={idx}>{month}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Year
                </label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="input"
                  min="2020"
                  max="2030"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.sendEmail}
                onChange={(e) => setFormData({ ...formData, sendEmail: e.target.checked })}
                className="rounded border-surface-600 bg-surface-700 text-primary-500"
              />
              <span className="text-sm text-surface-300">Send invoice email after generation</span>
            </label>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Generating...' : 'Generate Invoice'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// Main Invoices Page
export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [eligibleCompanies, setEligibleCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    year: new Date().getFullYear(),
    month: ''
  });
  
  // Modals
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    loadInvoices();
    loadStats();
    loadEligibleCompanies();
  }, [pagination.page, filters]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: 20,
        ...filters
      };
      if (params.status === 'all') delete params.status;
      if (!params.month && params.month !== 0) delete params.month;
      
      const response = await adminApi.getInvoices(params);
      setInvoices(response.data.invoices);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminApi.getInvoiceStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadEligibleCompanies = async () => {
    try {
      const response = await adminApi.getEligibleCompanies();
      setEligibleCompanies(response.data.companies);
    } catch (error) {
      console.error('Failed to load eligible companies:', error);
    }
  };

  const handleAction = async (action, invoice) => {
    switch (action) {
      case 'download':
        try {
          const response = await adminApi.downloadInvoicePDF(invoice._id);
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${invoice.invoiceNumber}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          toast.error('Failed to download PDF');
        }
        break;
        
      case 'resend':
        try {
          await adminApi.resendInvoice(invoice._id, {});
          toast.success(`Invoice sent to ${invoice.customerSnapshot?.invoiceEmail || invoice.sentTo}`);
          loadInvoices();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to send invoice');
        }
        break;
        
      case 'markPaid':
        setSelectedInvoice(invoice);
        setShowMarkPaidModal(true);
        break;
        
      default:
        break;
    }
  };

  const handleMarkPaid = async (invoiceId, paymentData) => {
    try {
      await adminApi.markInvoicePaid(invoiceId, paymentData);
      toast.success('Invoice marked as paid');
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to mark invoice as paid');
      throw error;
    }
  };

  const handleGenerateInvoice = async (formData) => {
    try {
      const response = await adminApi.generateInvoice(formData);
      toast.success(response.data.message || 'Invoice generated successfully');
      loadInvoices();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate invoice');
      throw error;
    }
  };

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Invoices</h1>
          <p className="text-surface-400 mt-1">Manage enterprise customer invoices</p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Generate Invoice
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-500/20 rounded-lg">
                <DocumentTextIcon className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-surface-100">{stats.draft?.count || 0}</p>
                <p className="text-sm text-surface-400">Draft</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <PaperAirplaneIcon className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-surface-100">{stats.sent?.count || 0}</p>
                <p className="text-sm text-surface-400">Sent</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <CheckCircleIcon className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-surface-100">{stats.paid?.count || 0}</p>
                <p className="text-sm text-surface-400">Paid</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-surface-100">{stats.overdue?.count || 0}</p>
                <p className="text-sm text-surface-400">Overdue</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500/20 rounded-lg">
                <CurrencyDollarIcon className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-surface-100">
                  {((stats.sent?.total || 0) + (stats.overdue?.total || 0)).toLocaleString()}
                </p>
                <p className="text-sm text-surface-400">Outstanding</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="input pl-10"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="input w-40"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
            className="input w-32"
          >
            {[2024, 2025, 2026, 2027].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select
            value={filters.month}
            onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            className="input w-32"
          >
            <option value="">All Months</option>
            {months.map((month, idx) => (
              <option key={idx} value={idx}>{month}</option>
            ))}
          </select>
          <button
            onClick={loadInvoices}
            className="btn-secondary"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-surface-400">
            <DocumentTextIcon className="w-12 h-12 mb-3 opacity-50" />
            <p>No invoices found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-surface-700/50">
                <th className="text-left text-xs font-medium text-surface-400 p-4">Invoice #</th>
                <th className="text-left text-xs font-medium text-surface-400 p-4">Company</th>
                <th className="text-left text-xs font-medium text-surface-400 p-4">Period</th>
                <th className="text-right text-xs font-medium text-surface-400 p-4">Amount</th>
                <th className="text-center text-xs font-medium text-surface-400 p-4">Status</th>
                <th className="text-left text-xs font-medium text-surface-400 p-4">Due Date</th>
                <th className="text-right text-xs font-medium text-surface-400 p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr
                  key={invoice._id}
                  className="border-t border-surface-700 hover:bg-surface-700/30 cursor-pointer"
                  onClick={() => {
                    setSelectedInvoice(invoice);
                    setShowDetailsModal(true);
                  }}
                >
                  <td className="p-4">
                    <span className="font-mono text-sm text-primary-400">{invoice.invoiceNumber}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-surface-200">{invoice.customerSnapshot?.companyName || invoice.company?.name}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-surface-300">{invoice.billingPeriodDisplay}</span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="font-semibold text-surface-100">
                      {formatCurrency(invoice.total, invoice.currency?.symbol, invoice.currency?.code)}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <StatusBadge status={invoice.isOverdue && invoice.status === 'sent' ? 'overdue' : invoice.status} />
                  </td>
                  <td className="p-4">
                    <span className={`text-sm ${invoice.isOverdue ? 'text-red-400' : 'text-surface-400'}`}>
                      {formatDate(invoice.dueDate)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleAction('download', invoice)}
                        className="p-2 hover:bg-surface-600 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 text-surface-400" />
                      </button>
                      {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                        <>
                          <button
                            onClick={() => handleAction('resend', invoice)}
                            className="p-2 hover:bg-surface-600 rounded-lg transition-colors"
                            title="Send Invoice"
                          >
                            <PaperAirplaneIcon className="w-4 h-4 text-blue-400" />
                          </button>
                          <button
                            onClick={() => handleAction('markPaid', invoice)}
                            className="p-2 hover:bg-surface-600 rounded-lg transition-colors"
                            title="Mark as Paid"
                          >
                            <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-surface-700">
            <p className="text-sm text-surface-400">
              Showing {((pagination.page - 1) * 20) + 1} to {Math.min(pagination.page * 20, pagination.total)} of {pagination.total} invoices
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                className="btn-secondary btn-sm"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.pages}
                className="btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <InvoiceDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        invoice={selectedInvoice}
        onAction={handleAction}
      />

      <MarkPaidModal
        isOpen={showMarkPaidModal}
        onClose={() => setShowMarkPaidModal(false)}
        invoice={selectedInvoice}
        onConfirm={handleMarkPaid}
      />

      <GenerateInvoiceModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        companies={eligibleCompanies}
        onGenerate={handleGenerateInvoice}
      />
    </div>
  );
}
